import { action } from '@ember/object';
import Service from '@ember/service';
import { service } from '@ember/service';
import { didCancel, task } from 'ember-concurrency';
import type { ChatMessage, ChatSuggestion } from 'walden-fast/types/chat';
import { tracked } from '@glimmer/tracking';
import type UserService from './user';
import type { IntlService } from 'ember-intl';
import type ModalService from './modal';
import { SERVICE_NAMES } from 'walden-fast/utils/constants/services';
import type FastApiService from './fast-api';

export default class ChatService extends Service {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('intl') declare intlService: IntlService;
  @service('modal') declare modalService: ModalService;
  @service('user') declare userService: UserService;

  @tracked
  messages: ChatMessage[] = [];

  get isLoading() {
    return this.fetchMessages.isRunning;
  }

  get suggestions(): ChatSuggestion[] {
    return [
      {
        text: this.intlService.t('components.chatSuggestions.questions.first'),
        immediate: true,
      },
      {
        text: this.intlService.t('components.chatSuggestions.questions.second'),
        immediate: false,
      },
      {
        text: this.intlService.t('components.chatSuggestions.questions.third'),
        immediate: false,
      },
      {
        text: this.intlService.t('components.chatSuggestions.questions.fourth'),
        immediate: true,
      },
      {
        text: this.intlService.t('components.chatSuggestions.questions.fifth'),
        immediate: true,
      },
    ];
  }

  @action
  async onNewMessage(message: string) {
    this.messages = [
      ...this.messages,
      {
        role: 'user',
        content: message,
        date: new Date(),
      },
    ];
    try {
      await this.fetchMessages.perform();
    } catch (e) {
      if (!didCancel(e)) {
        // re-throw the non-cancelation error
        throw e;
      }
    }
  }

  @action
  initialFetch() {
    if (!this.messages.length || this.messages.length === 1) {
      if (this.isEnabled) {
        this.messages = [
          {
            role: 'assistant',
            date: new Date(),
            content: this.intlService.t('services.waldenChat.hello.message', {
              user:
                this.userService.user?.firstName ||
                this.userService.user?.displayName,
            }),
          },
        ];
      } else {
        this.messages = [
          {
            role: 'assistant',
            content: this.intlService.t(
              'services.waldenChat.noAccess.message',
              {
                htmlSafe: true,
              },
            ),
            actions: [
              {
                label: this.intlService.t(
                  'services.waldenChat.noAccess.activate',
                  {
                    htmlSafe: true,
                  },
                ),
                variant: 'primary',
                onClick: () => {
                  this.modalService.open('activate-chat');
                },
              },
              {
                label: this.intlService.t(
                  'services.waldenChat.noAccess.moreInfo',
                  {
                    htmlSafe: true,
                  },
                ),
                variant: 'secondary',
                onClick: () => {},
              },
            ],
          },
        ];
      }
    }
  }

  get isEnabled() {
    return this.userService.canAccessService(SERVICE_NAMES.WALDEN_CHAT);
  }

  fetchMessages = task(async (): Promise<ChatMessage[]> => {
    try {
      const requestBody: ChatMessage[] = this.messages.map((message) => ({
        role: message.role,
        content: message.content,
      }));

      const message = await this.fastApiService.post<
        ChatMessage,
        ChatMessage[]
      >('/walden-chat/chat', requestBody);
      if (message) {
        this.messages = [...this.messages, { ...message, date: new Date() }];
      }
    } catch (error) {
      console.error(error);
      const errorMessage = this.intlService.t(
        'services.waldenChat.error.message',
      );
      this.messages = [
        ...this.messages,
        {
          role: 'assistant',
          date: new Date(),
          content: errorMessage,
        },
      ];
    }

    return this.messages;
  });
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:chat')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('chat') declare altName: ChatService;`.
declare module '@ember/service' {
  interface Registry {
    chat: ChatService;
  }
}
